import { useState, useEffect } from 'react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/contexts/AuthContext';
import { gitlabApi, GitLabConnection, GitLabGroup, GitLabProject } from '@/lib/api/gitlab';
import toast from 'react-hot-toast';
import { Gitlab, Loader2, Plus, Search, Lock, Globe, Shield, Building, User, AlertTriangle } from 'lucide-react';

interface GitLabImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (projectId: string) => void;
}

type Step = 'select-connection' | 'select-repo' | 'configure';

export default function GitLabImportModal({
  isOpen,
  onClose,
  onSuccess,
}: GitLabImportModalProps) {
  const { getIdToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<Step>('select-connection');

  // Connection state
  const [connections, setConnections] = useState<GitLabConnection[]>([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState<string>('');
  const [selectedConnection, setSelectedConnection] = useState<GitLabConnection | null>(null);

  // Repo selection state
  const [groups, setGroups] = useState<GitLabGroup[]>([]);
  const [selectedOwner, setSelectedOwner] = useState<string>('');
  const [repositories, setRepositories] = useState<GitLabProject[]>([]);
  const [selectedRepo, setSelectedRepo] = useState<GitLabProject | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingRepos, setLoadingRepos] = useState(false);

  // Config state
  const [branch, setBranch] = useState('main');
  const [branches, setBranches] = useState<string[]>([]);
  const [projectName, setProjectName] = useState('');

  useEffect(() => {
    if (isOpen) {
      setStep('select-connection');
      loadConnections();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedConnectionId) {
      const conn = connections.find((c) => c.id === selectedConnectionId);
      setSelectedConnection(conn || null);
      if (conn) {
        loadGroups(selectedConnectionId);
        setSelectedOwner(conn.metadata?.username || '');
      }
    }
  }, [selectedConnectionId, connections]);

  useEffect(() => {
    if (selectedConnectionId && selectedOwner) {
      loadRepositories();
    }
  }, [selectedConnectionId, selectedOwner]);

  const loadConnections = async () => {
    try {
      const token = await getIdToken();
      if (!token) return;
      const data = await gitlabApi.listConnections(token);
      setConnections(data);
      if (data.length > 0 && !selectedConnectionId) {
        setSelectedConnectionId(data[0].id);
      }
    } catch (error) {
      console.error('Failed to load GitLab connections', error);
    }
  };

  const loadGroups = async (connectionId: string) => {
    try {
      const token = await getIdToken();
      if (!token) return;
      const data = await gitlabApi.listGroups(token, connectionId);
      setGroups(data);
    } catch (error) {
      console.error('Failed to load groups', error);
    }
  };

  const loadRepositories = async () => {
    setLoadingRepos(true);
    try {
      const token = await getIdToken();
      if (!token) return;
      const repos = await gitlabApi.listRepositories(
        token,
        selectedConnectionId,
        selectedOwner,
        undefined,
        undefined,
        searchQuery || undefined,
      );
      setRepositories(repos);
    } catch (error) {
      console.error('Failed to load repositories', error);
    } finally {
      setLoadingRepos(false);
    }
  };

  const loadBranches = async (owner: string, repo: string) => {
    try {
      const token = await getIdToken();
      if (!token) return;
      const branchList = await gitlabApi.listBranches(
        token,
        selectedConnectionId,
        owner,
        repo,
      );
      setBranches(branchList.map((b: { name: string }) => b.name));
    } catch (error) {
      console.error('Failed to load branches', error);
    }
  };

  const handleConnectGitLab = async () => {
    try {
      setLoading(true);
      const token = await getIdToken();
      if (!token) {
        toast.error('Please sign in to continue');
        return;
      }
      const returnPath = '/dashboard/editor?openGitlabImport=true';
      const { authUrl } = await gitlabApi.initiateOAuth(
        token,
        undefined,
        undefined,
        returnPath,
      );
      window.location.href = authUrl;
    } catch (error) {
      console.error('Failed to initiate GitLab OAuth', error);
      toast.error('Failed to connect GitLab');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRepo = async (repo: GitLabProject) => {
    setSelectedRepo(repo);
    setProjectName(repo.name);
    setBranch(repo.default_branch);
    // Extract owner and name from path_with_namespace
    const parts = repo.path_with_namespace.split('/');
    const repoName = parts.pop() || repo.name;
    const owner = parts.join('/');
    await loadBranches(owner, repoName);
    setStep('configure');
  };

  const handleImport = async () => {
    if (!selectedRepo || !selectedConnectionId) {
      toast.error('Please select a repository');
      return;
    }

    try {
      setLoading(true);
      const token = await getIdToken();
      if (!token) {
        toast.error('Please sign in to continue');
        return;
      }

      const parts = selectedRepo.path_with_namespace.split('/');
      const repoName = parts.pop() || selectedRepo.name;
      const owner = parts.join('/');

      const project = await gitlabApi.importRepo(token, {
        connectionId: selectedConnectionId,
        owner,
        repoName,
        branch,
        projectName: projectName || selectedRepo.name,
      });
      toast.success('Repository imported successfully!');
      onSuccess(project.id);
      onClose();
    } catch (error: any) {
      console.error('Failed to import repository', error);
      toast.error(error.response?.data?.message || 'Failed to import repository');
    } finally {
      setLoading(false);
    }
  };

  const filteredRepos = searchQuery
    ? repositories.filter(
        (r) =>
          r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          r.description?.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : repositories;

  const getVisibilityIcon = (visibility: string) => {
    switch (visibility) {
      case 'private':
        return <Lock className="h-4 w-4 text-muted-foreground" />;
      case 'internal':
        return <Shield className="h-4 w-4 text-muted-foreground" />;
      default:
        return <Globe className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const renderSelectConnection = () => (
    <div className="space-y-4">
      {connections.length === 0 ? (
        <div className="text-center py-8">
          <Gitlab className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-4">No GitLab accounts connected</p>
          <Button onClick={handleConnectGitLab} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Gitlab className="h-4 w-4 mr-2" />
            )}
            Connect GitLab Account
          </Button>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            <Label>GitLab Account</Label>
            <Select value={selectedConnectionId} onValueChange={setSelectedConnectionId}>
              <SelectTrigger>
                <SelectValue placeholder="Select GitLab account" />
              </SelectTrigger>
              <SelectContent>
                {connections.map((conn) => (
                  <SelectItem key={conn.id} value={conn.id}>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-5 w-5">
                        <AvatarImage src={conn.metadata?.avatar_url} />
                        <AvatarFallback>
                          {conn.metadata?.username?.[0]?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span>{conn.metadata?.username || conn.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleConnectGitLab}
            disabled={loading}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add another account
          </Button>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={() => setStep('select-repo')}
              disabled={!selectedConnectionId}
            >
              Next
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const renderSelectRepo = () => (
    <div className="space-y-4">
      {/* Owner selection */}
      <div className="space-y-2">
        <Label>Repository Owner</Label>
        <Select value={selectedOwner} onValueChange={setSelectedOwner}>
          <SelectTrigger>
            <SelectValue placeholder="Select owner" />
          </SelectTrigger>
          <SelectContent>
            {selectedConnection?.metadata?.username && (
              <SelectItem value={selectedConnection.metadata.username}>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>{selectedConnection.metadata.username}</span>
                  <span className="text-muted-foreground text-xs">(Personal)</span>
                </div>
              </SelectItem>
            )}
            {groups.map((group) => (
              <SelectItem key={group.id} value={group.full_path}>
                <div className="flex items-center gap-2">
                  <Avatar className="h-4 w-4">
                    <AvatarImage src={group.avatar_url} />
                    <AvatarFallback>
                      <Building className="h-3 w-3" />
                    </AvatarFallback>
                  </Avatar>
                  <span>{group.full_path}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search repositories..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Repository list */}
      <div className="border rounded-lg max-h-[300px] overflow-y-auto">
        {loadingRepos ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : filteredRepos.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {searchQuery
              ? 'No repositories match your search'
              : 'No repositories found'}
          </div>
        ) : (
          <div className="divide-y">
            {filteredRepos.map((repo) => (
              <button
                key={repo.id}
                onClick={() => handleSelectRepo(repo)}
                className="w-full px-4 py-3 text-left hover:bg-muted transition-colors"
              >
                <div className="flex items-center gap-2">
                  {getVisibilityIcon(repo.visibility)}
                  <span className="font-medium">{repo.name}</span>
                </div>
                {repo.description && (
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                    {repo.description}
                  </p>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-between gap-2 pt-4">
        <Button variant="outline" onClick={() => setStep('select-connection')}>
          Back
        </Button>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );

  const renderConfigure = () => (
    <div className="space-y-4">
      {selectedRepo && (
        <div className="bg-muted p-4 rounded-lg">
          <div className="flex items-center gap-2">
            <Gitlab className="h-4 w-4" />
            <span className="font-medium">{selectedRepo.path_with_namespace}</span>
            {getVisibilityIcon(selectedRepo.visibility)}
          </div>
          {selectedRepo.description && (
            <p className="text-sm text-muted-foreground mt-1">
              {selectedRepo.description}
            </p>
          )}
        </div>
      )}

      {/* Deployment requirements warning */}
      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-amber-500 mb-1">
              Deployment Requirements
            </p>
            <p className="text-muted-foreground mb-2">
              Projects can be built and deployed on our platform if they follow:
            </p>
            <ul className="text-muted-foreground space-y-1 text-xs list-disc list-inside">
              <li>
                Vite project with{' '}
                <code className="bg-muted px-1 rounded">npm run build</code>{' '}
                outputting to{' '}
                <code className="bg-muted px-1 rounded">dist/</code>
              </li>
              <li>
                Dev server on port{' '}
                <code className="bg-muted px-1 rounded">5173</code> with{' '}
                <code className="bg-muted px-1 rounded">strictPort: true</code>
              </li>
              <li>
                <code className="bg-muted px-1 rounded">server.host: true</code>{' '}
                in vite.config
              </li>
              <li>
                <code className="bg-muted px-1 rounded">
                  X-Frame-Options: ALLOWALL
                </code>{' '}
                header for preview
              </li>
              <li>Static SPA (no SSR or server-side rendering)</li>
            </ul>
            <p className="text-muted-foreground mt-2 text-xs">
              Other projects (Next.js, Python, etc.) can still be developed but
              cannot be deployed.
            </p>
          </div>
        </div>
      </div>

      {/* Project name */}
      <div className="space-y-2">
        <Label>Project Name</Label>
        <Input
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          placeholder="My Project"
        />
      </div>

      {/* Branch */}
      <div className="space-y-2">
        <Label>Branch</Label>
        <Select value={branch} onValueChange={setBranch}>
          <SelectTrigger>
            <SelectValue placeholder="Select branch" />
          </SelectTrigger>
          <SelectContent>
            {branches.length > 0 ? (
              branches.map((b) => (
                <SelectItem key={b} value={b}>
                  {b}
                </SelectItem>
              ))
            ) : (
              <SelectItem value={branch}>{branch}</SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>

      <div className="flex justify-between gap-2 pt-4">
        <Button variant="outline" onClick={() => setStep('select-repo')}>
          Back
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleImport} disabled={loading || !projectName}>
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : null}
            Import Repository
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gitlab className="h-5 w-5" />
            Import from GitLab
          </DialogTitle>
          <DialogDescription>
            {step === 'select-connection' &&
              'Select a GitLab account to import from'}
            {step === 'select-repo' && 'Choose a repository to import'}
            {step === 'configure' && 'Configure your import settings'}
          </DialogDescription>
        </DialogHeader>

        {step === 'select-connection' && renderSelectConnection()}
        {step === 'select-repo' && renderSelectRepo()}
        {step === 'configure' && renderConfigure()}
      </DialogContent>
    </Dialog>
  );
}
